import React, { useState, useRef, useCallback } from 'react';
import classes from './index.module.scss';

/**
 * Props interface for the AddTag component
 */
interface Props {
	/** Callback function when a new tag is added */
	onAddMore?: (value: string) => any;
	/** Default label text for the add button */
	label?: string;
}

/**
 * AddTag component
 * Renders a button that transforms into an input field for adding new tags
 * Supports keyboard navigation and submission
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const AddTag = ({ onAddMore, label = '+ Add more' }: Props) => {
	// Current input text
	const [title, setTitle] = useState(label);
	// Reference to the input element for focusing and selection
	const inputRef = useRef<HTMLInputElement>(null);

	/**
	 * Select all text in the input field when focused
	 */
	const selectText = () => {
		if (!inputRef.current) {
			return;
		}
		inputRef.current.select();
	};

	/**
	 * Handle keyboard events in the input field
	 * Supports Escape to cancel and Enter to submit
	 *
	 * @param {React.KeyboardEvent<HTMLInputElement>} e - Keyboard event
	 */
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			// Cancel on Escape key
			if (e.key === 'Escape') {
				inputRef.current?.blur();
			}
			// Submit on Enter key
			if (e.key === 'Enter') {
				inputRef.current?.blur();

				// Call the callback if text is not empty
				if (onAddMore && title.trim().length > 0) {
					onAddMore(title);
				}
			}
		},
		[title, onAddMore]
	);

	/**
	 * Reset the input text to the default label when focus is lost
	 */
	const handleBlur = useCallback(() => {
		setTitle(label);
	}, [setTitle, label]);

	return (
		<div className={classes['addMoreTag']}>
			{/* Input field for adding new tags */}
			<input
				// Dynamic width based on content length
				style={{ width: title.length * 9 + 'px' }}
				ref={inputRef}
				onClick={() => selectText()}
				onFocus={() => selectText()}
				onKeyDown={handleKeyDown}
				onBlur={handleBlur}
				className={classes['addMoreTag-title']}
				value={title}
				onChange={({ target: { value } }) => setTitle(value)}
				// Accessibility attributes
				aria-label="Add new tag"
				placeholder={label}
			/>
		</div>
	);
};

export default AddTag;
